/**
 * Header/index.js
 * desc: header element and states
 * note: presentational component with internal state; the Sidebar carries the brand, so the header opens with the
 *       breadcrumb of the current location, and hosts the order schema picker on the routes that declare one (routes.ts)
 */
'use strict';

import { lazy, Suspense } from 'react';
import { AvatarIcon, DocumentationIcon, HelpIcon } from '../Icon';
import { hasAccess, logout } from '../../utilities/authentication';
import { trackEvent } from '../../utilities/analytics';
import { translate } from '../../utilities/localization';
import PathBreadcrumb from '../PathBreadcrumb';
import { findRoute } from '../../router/utilities';
import constants from '../../settings/constants';
import './styles.css';
import Popover from '../Popover';
import { useLocation } from 'react-router';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../../store/selectors/users';

// loaded on demand: a handful of routes show the picker, and its order-review data graph is not for the app shell
const OrderSchemaPicker = lazy(() => import('../OrderSchemaPicker'));

const Header = () => {
    //*************************************************
    //*****   Data   **********************************
    //*************************************************
    // Store state
    const user = useSelector(selectCurrentUser);
    const { pathname } = useLocation();

    // Variables
    const userPermissions = user?.permissions || [];
    const hasOrderSchemaPicker = !!findRoute(pathname)?.orderSchemaPicker;



    //*************************************************
    //*****   Public Functions   **********************
    //*************************************************

    const handleDocumentationIconClick = () => trackEvent('Global: Header - Documentation Icon (Click)');

    const handleHelpIconClick = () => trackEvent('Global: Header - Help Icon (Click)');

    const handleLogoutClick = () => {
        trackEvent('Global: Header - Logout Link (Click)');
        logout();
    };
    

    
    //*************************************************
    //*****   Render   ********************************
    //*************************************************
    return (
        <header id="header">
            <PathBreadcrumb/>

            { hasOrderSchemaPicker ? <Suspense fallback={ null }><OrderSchemaPicker/></Suspense> : null }

            <span className="actions">
                <a className="documentation" href={ constants.DOCUMENTATION_URL } onClick={ handleDocumentationIconClick } rel="noreferrer" target="_blank">
                    <DocumentationIcon/>
                </a>

                { hasAccess([ 'feature-support:view' ], userPermissions) ?
                    <a className="help" href={ constants.SUPPORT_LINK } onClick={ handleHelpIconClick } rel="noreferrer" target="_blank">
                        <HelpIcon/>
                    </a>
                    :
                    null
                }

                <Popover
                    alignOffset={ -150 }
                    asChild={ true }
                    sideOffset={ -3 }
                    content={ <menu className="avatar-menu">
                        <button type="button" className="logout" onClick={ handleLogoutClick }>{ translate('header.menu.logout') }</button>
                    </menu> }
                >
                    <div><AvatarIcon /></div>
                </Popover>
            </span>
        </header>
    ); 
};


export default Header;
